import { ArrowDown, ArrowUp } from "lucide-react";
import { Button } from "@/components/ui/button.jsx";
import { cn } from "@/lib/utils.js";
import {
  steelChromeButton,
  steelChromeButtonIdle,
} from "@/lib/status-styles.js";

/** @typedef {import("@/components/root-cause-analysis/rca-measurement-sort.js").RcaMeasurementSortMode} RcaMeasurementSortMode */
/** @typedef {import("@/components/root-cause-analysis/rca-measurement-sort.js").RcaMeasurementSortDirection} RcaMeasurementSortDirection */

const SORT_BUTTON_BASE =
  "h-8 gap-1 rounded-md px-3 text-xs font-normal shadow-none";

/**
 * @param {{
 *   sortMode: RcaMeasurementSortMode,
 *   direction: RcaMeasurementSortDirection,
 *   active: boolean,
 * }} props
 */
function SortDirectionIcon({ sortMode, direction, active }) {
  if (!active) return null;
  const Icon =
    sortMode === "alphabetical"
      ? direction === "asc"
        ? ArrowDown
        : ArrowUp
      : direction === "desc"
        ? ArrowDown
        : ArrowUp;
  return <Icon className='h-3.5 w-3.5 shrink-0' aria-hidden />;
}

/**
 * Sort controls for RCA measurement section lists. The active button shows an
 * arrow for direction; clicking it again reverses direction. Clicking the
 * inactive button switches sort mode (A–Z defaults to ascending).
 *
 * @param {{
 *   sortMode: RcaMeasurementSortMode,
 *   onSortModeChange: (mode: RcaMeasurementSortMode) => void,
 *   sortDirection: RcaMeasurementSortDirection,
 *   onSortDirectionChange: (direction: RcaMeasurementSortDirection) => void,
 *   scoreLabel?: string,
 *   className?: string,
 * }} props
 */
export default function RcaMeasurementSortToolbar({
  sortMode,
  onSortModeChange,
  sortDirection,
  onSortDirectionChange,
  scoreLabel = "Score",
  className,
}) {
  /**
   * @param {RcaMeasurementSortMode} mode
   */
  function handleModeClick(mode) {
    if (sortMode === mode) {
      onSortDirectionChange(sortDirection === "desc" ? "asc" : "desc");
      return;
    }
    onSortModeChange(mode);
    onSortDirectionChange(mode === "alphabetical" ? "asc" : "desc");
  }

  const scoreActive = sortMode === "score";
  const alphaActive = sortMode === "alphabetical";
  const scoreDirectionLabel =
    sortDirection === "desc" ? "descending" : "ascending";
  const alphaDirectionLabel = sortDirection === "desc" ? "Z to A" : "A to Z";

  return (
    <div
      className={cn("flex flex-wrap items-center gap-2", className)}
      role='group'
      aria-label='Sort measurements'>
      <span className='text-xs text-muted-foreground'>Sort:</span>
      <Button
        type='button'
        size='sm'
        variant='outline'
        className={cn(
          SORT_BUTTON_BASE,
          scoreActive ? steelChromeButton : steelChromeButtonIdle,
          "text-xs"
        )}
        onClick={() => handleModeClick("score")}
        aria-pressed={scoreActive}
        aria-label={
          scoreActive
            ? `Sort by ${scoreLabel}, ${scoreDirectionLabel}. Press to reverse direction.`
            : `Sort by ${scoreLabel}`
        }>
        <span>{scoreLabel}</span>
        <SortDirectionIcon
          sortMode='score'
          direction={sortDirection}
          active={scoreActive}
        />
      </Button>
      <Button
        type='button'
        size='sm'
        variant='outline'
        className={cn(
          SORT_BUTTON_BASE,
          alphaActive ? steelChromeButton : steelChromeButtonIdle,
          "text-xs"
        )}
        onClick={() => handleModeClick("alphabetical")}
        aria-pressed={alphaActive}
        aria-label={
          alphaActive
            ? `Sort alphabetically, ${alphaDirectionLabel}. Press to reverse direction.`
            : "Sort alphabetically"
        }>
        <span>A–Z</span>
        <SortDirectionIcon
          sortMode='alphabetical'
          direction={sortDirection}
          active={alphaActive}
        />
      </Button>
    </div>
  );
}
